import { useEffect, useRef, useState } from "react";
import { fetchMachineData, hasMachineData } from "./fetch-machine-data";
import type {
  ModeColor,
  ModeConfig,
  MachineDataResponse,
  MachineOverviewDefinition,
  MachineState,
} from "@src/types/factory-dashboard";
import { getQueryLastTimeseriesByMetadataUseCase } from "@src/cloud-connect/use-cases/get-query-last-timeseries-by-metadata";
import store from "@src/reducers/store";
import { querySourceByIdUseCase } from "@src/cloud-connect/client-configuration/use-cases/query-source-by-id-use-case";
import { useI18N } from "@src/language/language-context";
import { formatTextValue } from "../../../cloud-connect/utils/format-text-value";

export type { ModeColor } from "@src/types/factory-dashboard";

const FALLBACK_QUERY_PARAMS = {
  limit: 1,
  duration: "PT30M",
  limit_order: "DESC" as const,
};

const createEmptyMachineState = (time = ""): MachineState => ({
  opMode: { value: null, time },
  actCyclesDone: { value: null, time: "" },
  setCyclesCompleteToDo: { value: null, time: "" },
  productionDatasetName: { value: null, time: "" },
  moldCatActualDescription: { value: null, time: "" },
  filename: { value: null, time: "" },
  customer: { value: null, time: "" },
  material: { value: null, time: "" },
});

const extractFallbackTimestamp = (data: unknown): string => {
  if (!data || typeof data !== "object") {
    return "";
  }

  const maybeEntry = data as {
    values?: Array<{ time?: string }>;
    time?: string;
  };

  return maybeEntry.values?.[0]?.time ?? maybeEntry.time ?? "";
};

export interface UseMachineOverviewResult {
  machineTitle: string;
  isLoading: boolean;
  hasData: boolean;
  modeLabel: string;
  modeColor: ModeColor;
  cyclesDone: number;
  cycleLimit: number;
  progress: number;
  animatedProgressPercentage: number;
  productionDataset: string;
  moldCatActualDescription?: string;
  filename?: string;
  lastUpdatedTime: string;
  customer?: string;
  material?: string;
}

export const useMachineOverview = (
  machine: MachineOverviewDefinition,
): UseMachineOverviewResult => {
  const i18n = useI18N();
  const { sourceId, nodes, modeMapping } = machine;
  const [machineState, setMachineState] = useState<MachineState | null>(null);
  const [machineTitle, setMachineTitle] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [animatedProgressPercentage, setAnimatedProgressPercentage] =
    useState(0);

  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    const loadMachineName = async () => {
      const namespace =
        store.getState().yourSlice.authContext?.customerNamespace;

      if (!namespace) {
        setMachineTitle("");
        return;
      }

      const sourceInfo = await querySourceByIdUseCase(sourceId, false);
      setMachineTitle(sourceInfo?.data?.name ?? "");
    };

    loadMachineName().catch((err) => {
      console.error(`Error fetching source info for machine ${sourceId}:`, err);
      setMachineTitle("");
    });
  }, [sourceId]);

  useEffect(() => {
    const loadMachineData = async () => {
      if (isInitialLoadRef.current) {
        setIsLoading(true);
      }

      try {
        const res: MachineDataResponse = await fetchMachineData(
          sourceId,
          nodes,
        );
        const [, state] = Object.entries(res)[0] ?? [];

        if (hasMachineData(state)) {
          setMachineState(state);
          return;
        }

        const fallbackResponse = await getQueryLastTimeseriesByMetadataUseCase({
          group: sourceId,
          source: nodes.opMode,
          ...FALLBACK_QUERY_PARAMS,
        });

        if (!fallbackResponse.success) {
          console.error(
            `Failed to load fallback timestamp for machine ${sourceId}`,
          );
        }

        const fallbackTimestamp = fallbackResponse.success
          ? extractFallbackTimestamp(fallbackResponse.data)
          : "";

        setMachineState(createEmptyMachineState(fallbackTimestamp));
      } catch (err) {
        console.error(`Error fetching data for machine ${sourceId}:`, err);
      } finally {
        if (isInitialLoadRef.current) {
          isInitialLoadRef.current = false;
          setIsLoading(false);
        }
      }
    };

    loadMachineData();

    const intervalId = setInterval(loadMachineData, 5000);

    return () => clearInterval(intervalId);
  }, [nodes, sourceId]);

  const hasData =
    machineState !== null &&
    Object.values(machineState).some((v) => v?.value != null);

  const cyclesDone = Number(machineState?.actCyclesDone?.value ?? 0);
  const cycleLimit = Number(machineState?.setCyclesCompleteToDo?.value ?? 0);
  const lastUpdatedTime = machineState?.opMode?.time ?? "";
  const productionDataset = formatTextValue(
    machineState?.productionDatasetName?.value,
  );
  const moldCatActualDescription = formatTextValue(
    machineState?.moldCatActualDescription?.value,
  );
  const filename = formatTextValue(machineState?.filename?.value);
  const customer = formatTextValue(machineState?.customer?.value);
  const material = formatTextValue(machineState?.material?.value);
  const progress = cycleLimit > 0 ? (cyclesDone / cycleLimit) * 100 : 0;
  const progressPercentage = Math.max(0, Math.min(progress, 100));

  useEffect(() => {
    if (isLoading) {
      setAnimatedProgressPercentage(0);
      return;
    }

    setAnimatedProgressPercentage(0);

    const animationFrame = window.requestAnimationFrame(() => {
      setAnimatedProgressPercentage(progressPercentage);
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [isLoading, progressPercentage]);

  const modeValue = Number(machineState?.opMode?.value ?? 0);
  const mode = modeMapping[modeValue] as ModeConfig | undefined;

  const modeLabel = mode?.label ? i18n(mode.label) : "";
  const modeColor = mode?.color ?? { outline: "", background: "" };

  return {
    machineTitle,
    isLoading,
    hasData,
    modeLabel,
    modeColor,
    cyclesDone,
    cycleLimit,
    progress,
    animatedProgressPercentage,
    productionDataset,
    moldCatActualDescription,
    filename,
    customer,
    material,
    lastUpdatedTime,
  };
};
